import React, { useMemo, useState } from "react";
import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import { exportToExcel, ExcelData } from "../../../utils/excelExport";
import styles from "./supplierAwardList.module.scss";

interface SupplierAwardListProps {
  title?: string;
  data?: any[];
  supplierKey?: string;
  valueKey?: string;
  poCountKey?: string;
  countLabel?: string;
  valueFormatter?: (value: number) => string;
  itemsPerPage?: number;
  onDownload?: () => void;
}

const SupplierAwardList: React.FC<SupplierAwardListProps> = ({
  title = "Award by Suppliers",
  data = [],
  supplierKey = "supplier",
  valueKey = "value",
  poCountKey = "poCount",
  countLabel = "POs",
  valueFormatter,
  itemsPerPage = 5,
  onDownload,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const defaultColor = "#00d2aa";

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => ({
      supplier: item[supplierKey] || `Supplier ${index + 1}`,
      value: Number(item[valueKey] || 0),
      poCount: Number(item[poCountKey] || 0),
    }));
  }, [data, supplierKey, valueKey, poCountKey]);

  const displayData = useMemo(() => {
    return chartData.slice(0, itemsPerPage);
  }, [chartData, itemsPerPage]);

  const maxValue = useMemo(() => {
    if (chartData.length === 0) return 100;
    return Math.max(...chartData.map((item) => item.value), 1);
  }, [chartData]);

  const handleExpandClick = () => {
    setIsExpanded(true);
  };

  const handleCloseExpand = () => {
    setIsExpanded(false);
  };

  const formatValue = (value: number) => {
    if (valueFormatter) return valueFormatter(value);
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
      return;
    }

    const columns = [
      { header: supplierKey === "category" ? "Category" : "Supplier Name", key: "supplier", width: 40 },
      { header: "Total Award (USD)", key: "value", width: 20 },
    ];

    // Only add count column if we have count data
    if (chartData.some(item => item.poCount > 0)) {
      columns.push({ header: `${countLabel} Count`, key: "poCount", width: 15 });
    }

    const excelData: ExcelData = {
      sheetName: title,
      fileName: title.replace(/\s+/g, "_"),
      columns,
      data: chartData,
    };

    exportToExcel(excelData);
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
        </Box>
        <Box
          className={styles.listContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  const renderSupplierList = (dataToRender: typeof chartData, isModal = false) => (
    <Box className={isModal ? styles.modalListContainer : styles.listContainer}>
      {dataToRender.map((item, index) => (
        <Box key={index} className={styles.listItem}>
          <Box className={styles.supplierInfo}>
            <Typography className={styles.supplierName}>
              {item.supplier}
            </Typography>
            {item.poCount > 0 && (
              <Typography className={styles.poCount}>
                {item.poCount} {countLabel}
              </Typography>
            )}
          </Box>
          <Box className={styles.barSection}>
            <Box className={styles.barBackground}>
              <Box
                className={styles.barFill}
                style={{
                  width: `${(item.value / maxValue) * 100}%`,
                  background: defaultColor,
                }}
              />
            </Box>
          </Box>
          <Typography className={styles.value}>
            {formatValue(item.value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
          <Box className={styles.headerActions}>
            {chartData.length > itemsPerPage && (
              <IconButton
                onClick={handleExpandClick}
                size="small"
                sx={{
                  color: "#5b6b9e",
                  "&:hover": {
                    backgroundColor: "rgba(91, 107, 158, 0.1)",
                  },
                }}
                aria-label="expand"
              >
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              onClick={handleDownload}
              size="small"
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
              aria-label="download"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {renderSupplierList(displayData)}
      </Box>

      <Dialog
        open={isExpanded}
        onClose={handleCloseExpand}
        maxWidth={false}
        fullScreen
        className={styles.expandDialog}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Typography variant="h6" className={styles.dialogTitleText}>
            {title}
          </Typography>
          <Box className={styles.dialogActions}>
            <IconButton
              onClick={handleDownload}
              size="small"
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
              aria-label="download"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="close"
              onClick={handleCloseExpand}
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {renderSupplierList(chartData, true)}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupplierAwardList;
